import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Heart, MessageCircle, Share2, MoreVertical, Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { getGroupPosts, subscribeToGroupPosts, deletePost } from '@/lib/firebase/queries/posts';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { EditPostDialog } from './EditPostDialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import type { Post, Group } from '@/types/support';

interface GroupPostsProps {
  group: Group;
}

export function GroupPosts({ group }: GroupPostsProps) {
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [isOffline, setIsOffline] = useState(false);
  const [postToEdit, setPostToEdit] = useState<Post | null>(null);
  const [postToDelete, setPostToDelete] = useState<Post | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const { currentUser } = useAuth();
  const { t } = useLanguage();

  useEffect(() => {
    let unsubscribe: (() => void) | undefined;

    const initializePosts = async () => {
      try {
        // First try to get cached data
        const cachedPosts = await getGroupPosts(group.id);
        if (cachedPosts.length > 0) {
          setPosts(cachedPosts);
          setLoading(false);
        }

        // Set up real-time listener
        unsubscribe = subscribeToGroupPosts(
          group.id,
          (updatedPosts) => {
            setPosts(updatedPosts);
            setLoading(false);
            setIsOffline(false);
          },
          (error) => {
            console.error('Posts subscription error:', error);
            if (error.code === 'unavailable' || error.code === 'failed-precondition') {
              setIsOffline(true);
              if (posts.length === 0) {
                toast.warning(t('support.offlineMode'));
              }
            }
            setLoading(false);
          }
        );
      } catch (error: any) {
        console.error('Error initializing posts:', error);
        if (error.code === 'unavailable' || error.code === 'failed-precondition') {
          setIsOffline(true);
          if (posts.length === 0) {
            toast.warning(t('support.offlineMode'));
          }
        } else {
          toast.error(t('support.postsLoadError'));
        }
        setLoading(false);
      }
    };

    initializePosts();

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [group.id]);

  const handleDeletePost = async () => {
    if (!postToDelete) return;

    try {
      setIsDeleting(true);
      await deletePost(postToDelete.id);
      toast.success(t('support.postDeleted'));
    } catch (error) {
      console.error('Error deleting post:', error);
      toast.error(t('support.postDeleteError'));
    } finally {
      setIsDeleting(false);
      setPostToDelete(null);
    }
  };

  // Allow any authenticated user to modify posts
  const canModifyPost = () => {
    return !!currentUser;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-40">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (posts.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        {t('support.noPosts')}
      </div>
    );
  }

  return (
    <>
      <ScrollArea className="h-[600px] pr-4">
        <div className="space-y-6">
          {posts.map((post) => (
            <motion.div
              key={post.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="rounded-lg border bg-card p-6 space-y-4"
            >
              <div className="flex items-center justify-between gap-4">
                <div className="space-y-1">
                  <h3 className="font-semibold text-lg">{post.title}</h3>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <span>{post.userName}</span>
                    <span>•</span>
                    <span>{format(post.createdAt, 'PPp')}</span>
                  </div>
                </div>

                {canModifyPost() && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => setPostToEdit(post)}>
                        {t('support.editPost')}
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        onClick={() => setPostToDelete(post)}
                        className="text-red-600"
                      >
                        {t('support.deletePost')}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>

              <p className="text-muted-foreground whitespace-pre-wrap">{post.content}</p>

              <div className="flex items-center gap-4">
                <Button variant="ghost" size="sm">
                  <Heart className="h-4 w-4 mr-2" />
                  {post.likes}
                </Button>
                <Button variant="ghost" size="sm">
                  <MessageCircle className="h-4 w-4 mr-2" />
                  {post.comments}
                </Button>
                <Button variant="ghost" size="sm">
                  <Share2 className="h-4 w-4 mr-2" />
                  {t('support.share')}
                </Button>
              </div>
            </motion.div>
          ))}
        </div>
      </ScrollArea>

      {postToEdit && (
        <EditPostDialog
          post={postToEdit}
          open={!!postToEdit}
          onOpenChange={(open) => !open && setPostToEdit(null)}
          onSuccess={() => setPostToEdit(null)}
        />
      )}

      <AlertDialog open={!!postToDelete} onOpenChange={(open) => !open && setPostToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('support.deletePostTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('support.deletePostMessage')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>
              {t('common.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeletePost}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('common.deleting')}
                </>
              ) : (
                t('support.deletePost')
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}